import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Pizza, DollarSign, TrendingUp, Calculator, ChefHat,
  Upload, ArrowRight, BarChart3, Percent, Layers
} from 'lucide-react';

const FEATURES = [
  { icon: DollarSign, title: 'Food Cost Per Item', desc: 'Know exactly what each pizza, sandwich, or salad costs to make.', color: '#E23D28' },
  { icon: ChefHat, title: 'Recipe Builder', desc: 'Build recipes, assign ingredients, and see real-time costs.', color: '#FFC107' },
  { icon: Calculator, title: 'Labor Cost Per Item', desc: 'Factor in prep time, cook time, and loaded labor rates.', color: '#2E7D32' },
  { icon: TrendingUp, title: 'Suggested Pricing', desc: 'Get MSRP suggestions at 20%, 25%, 30%, and 35% food cost.', color: '#E23D28' },
  { icon: BarChart3, title: 'Break-Even Point', desc: 'See how many orders you need per day to cover your nut.', color: '#FFC107' },
  { icon: Percent, title: 'Specials Impact', desc: 'Run a special? See if it actually makes money or just noise.', color: '#2E7D32' },
  { icon: Layers, title: 'Yield Calculator', desc: 'How many pizzas can you make from your current inventory?', color: '#E23D28' },
  { icon: Upload, title: 'Upload Ingredients', desc: 'Upload CSV or XLSX. No data entry headaches.', color: '#FFC107' },
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FFF8E7]">
      {/* Header */}
      <header className="border-b-2 border-[#2A2A2A] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="landing-logo">
            <div className="w-12 h-12 bg-[#E23D28] rounded-full border-2 border-[#2A2A2A] flex items-center justify-center shadow-[3px_3px_0px_0px_rgba(42,42,42,1)]">
              <Pizza className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl leading-none text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>
                Mikey Mozzarella's
              </h1>
              <p className="text-sm text-[#E23D28]" style={{ fontFamily: "'Bebas Neue', cursive", letterSpacing: '0.1em' }}>
                Pizza-lator
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/login')} className="text-sm text-[#2A2A2A]/60 hover:text-[#E23D28] font-medium" data-testid="header-login-btn"
              style={{ fontFamily: "'Outfit', sans-serif" }}>
              Sign In
            </button>
            <button onClick={() => navigate('/register')} className="pizza-btn-secondary text-sm" data-testid="header-register-btn">
              Create Account
            </button>
            <button
              onClick={() => navigate('/wizard')}
              className="pizza-btn text-sm"
              data-testid="header-start-btn"
            >
              Start Free
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="retro-checker border-b-2 border-[#2A2A2A]" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="inline-block pizza-tag pizza-tag-red mb-6">
              Know Your Dough
            </div>
            <h1
              className="text-5xl sm:text-6xl lg:text-7xl text-[#2A2A2A] leading-[0.95] mb-6"
              style={{ fontFamily: "'Bebas Neue', cursive" }}
            >
              Stop Guessing What Your Pizza
              <span className="text-[#E23D28]"> Actually Costs.</span>
            </h1>
            <p className="text-base md:text-lg text-[#2A2A2A]/70 mb-10 max-w-2xl leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Upload your ingredients, build your recipes, add labor, and see exactly what each menu item costs,
              what you should charge, and how much profit you're really making.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => navigate('/wizard')}
                className="pizza-btn flex items-center gap-2"
                data-testid="hero-start-btn"
              >
                Start Calculating <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/upload')}
                className="pizza-btn-secondary flex items-center gap-2"
                data-testid="hero-upload-btn"
              >
                <Upload className="w-4 h-4" /> Upload Ingredient Sheet
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Section */}
      <section className="border-b-2 border-[#2A2A2A] bg-[#2A2A2A]" data-testid="pain-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-20">
          <div className="max-w-3xl mx-auto text-center">
            <h2
              className="text-4xl md:text-5xl text-white mb-4"
              style={{ fontFamily: "'Bebas Neue', cursive" }}
            >
              Most Restaurants Know <span className="text-[#FFC107]">Sales.</span>
            </h2>
            <h2
              className="text-4xl md:text-5xl text-white mb-8"
              style={{ fontFamily: "'Bebas Neue', cursive" }}
            >
              Not Enough Know <span className="text-[#E23D28]">Profit.</span>
            </h2>
            <p className="text-[#FFF8E7]/70 text-base md:text-lg mb-10" style={{ fontFamily: "'Outfit', sans-serif" }}>
              The Pizza-lator shows you the numbers that actually matter.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {['Food Cost Per Item', 'Labor Cost Per Item', 'Packaging Cost', 'Suggested MSRP',
                'Gross Profit', 'Break-Even Point', 'Specials Impact', 'Inventory Yield'
              ].map((item, i) => (
                <div
                  key={i}
                  className="bg-white/10 border border-white/20 rounded-lg p-3 text-white text-sm font-medium"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="border-b-2 border-[#2A2A2A]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-20">
          <div className="text-center mb-12">
            <h2
              className="text-4xl md:text-5xl text-[#2A2A2A] mb-3"
              style={{ fontFamily: "'Bebas Neue', cursive" }}
            >
              Everything You Need to
              <span className="text-[#E23D28]"> Know Your Numbers</span>
            </h2>
            <p className="text-[#2A2A2A]/60 text-base" style={{ fontFamily: "'Outfit', sans-serif" }}>
              A simple profit calculator for pizza shops and restaurants.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map(({ icon: Icon, title, desc, color }, i) => (
              <div
                key={i}
                className="pizza-card p-5"
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                <div
                  className="w-10 h-10 rounded-lg border-2 border-[#2A2A2A] flex items-center justify-center mb-3 shadow-[2px_2px_0px_0px_rgba(42,42,42,1)]"
                  style={{ background: color }}
                >
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <h3
                  className="text-lg text-[#2A2A2A] mb-1"
                  style={{ fontFamily: "'Bebas Neue', cursive", letterSpacing: '0.03em' }}
                >
                  {title}
                </h3>
                <p className="text-sm text-[#2A2A2A]/60" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#E23D28] border-b-2 border-[#2A2A2A]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-20 text-center">
          <h2
            className="text-4xl md:text-5xl text-white mb-4"
            style={{ fontFamily: "'Bebas Neue', cursive" }}
          >
            Ready to Know Your Dough?
          </h2>
          <p className="text-white/80 text-base md:text-lg mb-8 max-w-lg mx-auto" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Stop losing money on every slice. Start calculating your real costs today.
          </p>
          <button
            onClick={() => navigate('/wizard')}
            className="bg-[#FFC107] text-[#2A2A2A] border-2 border-[#2A2A2A] rounded-full px-10 py-4 font-bold uppercase tracking-widest shadow-[4px_4px_0px_0px_rgba(42,42,42,1)] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(42,42,42,1)] transition-all text-lg"
            style={{ fontFamily: "'Bebas Neue', cursive" }}
            data-testid="cta-start-btn"
          >
            Start Calculating Now <ArrowRight className="w-5 h-5 inline ml-2" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2A2A2A] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Pizza className="w-5 h-5 text-[#E23D28]" />
            <span className="text-white text-sm" style={{ fontFamily: "'Bebas Neue', cursive", letterSpacing: '0.1em' }}>
              Mikey Mozzarella's Pizza-lator
            </span>
          </div>
          <p className="text-white/40 text-xs mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Know your dough before you lose your dough.
          </p>
          <a href="/admin" rel="nofollow noindex" className="text-white/10 hover:text-white/30 text-[10px] transition-colors" data-testid="footer-admin-link">
            Admin
          </a>
        </div>
      </footer>
    </div>
  );
}
